import React, { useState, useEffect } from "react";
import { Card, PageHeader, Button, StatusBadge, DateRangePicker } from "../../../components/common";
import { useAppDispatch, useAppSelector, getAttendanceThunk } from "../../../redux";
import { formatYMD } from "../../../utils";
import "./StudentAttendance.css";

const StudentAttendance: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { records, isLoading } = useAppSelector((state) => state.attendance);

  // Compute default date range (1st day of current month to last day of current month)
  const now = new Date();
  const defaultStart = formatYMD(new Date(now.getFullYear(), now.getMonth(), 1));
  const defaultEnd = formatYMD(new Date(now.getFullYear(), now.getMonth() + 1, 0));

  const [startDate, setStartDate] = useState<string>(defaultStart);
  const [endDate, setEndDate] = useState<string>(defaultEnd);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);

  const fetchAttendance = (start = startDate, end = endDate, force = false) => {
    if (!user?.id) return;

    const selectQuery = `select id, Name, Teacher, Student, Date, Attendance_Status from Attendance where Student = '${user.id}' and Date between '${start}' and '${end}' order by Date desc limit 200`;

    dispatch(getAttendanceThunk({ query: selectQuery, force }));
  };

  useEffect(() => {
    if (user?.id) {
      fetchAttendance(defaultStart, defaultEnd);
    }
  }, [dispatch, user?.id]);

  // Outside click listener for DateRangePicker
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".date-picker-wrapper")) {
        setIsDatePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Compute Statistics directly from API records
  const totalSessions = records.length;
  const presentCount = records.filter((r) => r.Attendance_Status === "Present").length;
  const absentCount = records.filter((r) => r.Attendance_Status === "Absent").length;
  const attendancePercentage = totalSessions > 0 ? Math.round((presentCount / totalSessions) * 100) : 0;

  return (
    <div className="student-attendance-container">
      {/* Header Card */}
      <Card style={{ padding: "24px" }}>
        <PageHeader
          title="My Attendance"
          subtitle="View and track your attendance history across classes."
        />
      </Card>

      {/* Summary Statistics Cards */}
      <div className="attendance-stats-grid">
        <div className="stat-card">
          <span className="stat-label">Total Classes</span>
          <span className="stat-value">{totalSessions}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Present</span>
          <span className="stat-value present">{presentCount}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Absent</span>
          <span className="stat-value absent">{absentCount}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Attendance Rate</span>
          <span className="stat-value rate">{attendancePercentage}%</span>
        </div>
      </div>

      {/* Search & Date Filter Card */}
      <Card style={{ padding: "24px" }}>
        <h3 style={{ margin: "0 0 16px 0", fontSize: "1.1rem" }}>Attendance Records</h3>
        
        <div className="student-attendance-search-bar" style={{ marginBottom: "24px" }}>
          <DateRangePicker
            label="Date Range"
            startDate={startDate}
            endDate={endDate}
            isOpen={isDatePickerOpen}
            onToggle={() => setIsDatePickerOpen((prev) => !prev)}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
              setIsDatePickerOpen(false);
            }}
          />

          <div className="student-search-btn-container">
            <Button
              variant="secondary"
              size="medium"
              onClick={() => fetchAttendance(startDate, endDate, true)}
              disabled={isLoading}
            >
              {isLoading ? "Searching..." : "Search Attendance"}
            </Button>
          </div>
        </div>

        {/* Attendance Records List */}
        {records.length === 0 ? (
          <div className="no-student-records">
            No attendance records found for the selected date range.
          </div>
        ) : (
          <div className="student-attendance-records-list">
            {records.map((item) => (
              <div key={item.id} className="student-attendance-item">
                <div className="student-attendance-info">
                  <span className="student-attendance-title">
                    {item.Name || `Attendance - ${item.Date}`}
                  </span>
                  {/* <div className="student-attendance-meta">
                    <span className="meta-date">{formatDateIN(item.Date)}</span>
                  </div> */}
                </div>

                <StatusBadge variant={item.Attendance_Status === "Present" ? "success" : "error"}>
                  {item.Attendance_Status}
                </StatusBadge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default StudentAttendance;
